import { useApolloClient } from "@apollo/client";
import {
  createContext,
  Dispatch,
  FC,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";

import {
  GetEmployeeQuery,
  GetEmployeeQueryVariables,
  GetClientQuery,
  GetClientQueryVariables,
  GetUserQuery,
  GetUserQueryVariables,
  UserRole,
  GetAdminQuery,
  GetAdminQueryVariables,
  GetOrganizationQuery,
  GetOrganizationQueryVariables,
  useUpdateClientOrgMutation,
  MemberStatus,
} from "src/generated/graphql";
import { EnvConfig } from "src/utils/EnvConfig";
import { GetEmployee } from "./graphql/queries/Employee";
import { GetClient } from "./graphql/queries/Client";
import { GetUser } from "./graphql/queries/User";
import { ApolloClientContext } from "./ApolloClientContext";
import {
  CLIENT_ROUTES,
  CLIENT_TAB_LIST,
  PATH_NAMES,
  removeOrgLoginId,
  ROUTES,
  StorageKeyName,
  TAB_LIST,
} from "src/utils/constants";
import { GetAdmin } from "./graphql/queries/Admin";
import { GetOrganization } from "./graphql/queries/Organization";
import { ArrayElement } from "src/utils/helperTypes";
import { useBrandingContext } from "./context/BrandingContext";

type CurrentUser = GetUserQuery["userOne"];
type ClientData = NonNullable<GetClientQuery["clientOne"]>;
type EmployeeData = NonNullable<GetEmployeeQuery["employeeOne"]>;
type AdminData = NonNullable<GetAdminQuery["adminOne"]>;
type OrganizationData = NonNullable<GetOrganizationQuery["organizationOne"]>;
export type LocationData = ArrayElement<
  NonNullable<GetOrganizationQuery["organizationOne"]>["locations"]
>;

export type AppContextData = {
  env: EnvConfig | undefined;
  currentUser: CurrentUser;
  activeTabKey: string;
  currentClient: ClientData | undefined;
  currentEmployee: EmployeeData | undefined;
  setCurrentEmployee: Dispatch<
    SetStateAction<AppContextData["currentEmployee"]>
  >;
  currentAdmin: AdminData | undefined;
  currentPageTitle: string | undefined;
  isDarkMode: boolean;
  currentOrganization: OrganizationData | undefined;
  currentLocation: LocationData | undefined;
  workspaceDimensions: { width: number; height: number } | undefined;
  changeTab: (key: string, id?: string) => void;
  changeClientTab: (key: string, id?: string) => void;
  refetchUserData: () => void;
  setCurrentLocation: Dispatch<
    SetStateAction<AppContextData["currentLocation"]>
  >;
  setCurrentUser: Dispatch<SetStateAction<AppContextData["currentUser"]>>;
  setCurrentPageTitle: Dispatch<
    SetStateAction<AppContextData["currentPageTitle"]>
  >;
  setIsDarkMode: Dispatch<SetStateAction<AppContextData["isDarkMode"]>>;
  setWorkspaceDimensions: Dispatch<
    SetStateAction<AppContextData["workspaceDimensions"]>
  >;
  getEmployee: () => void;
  setCurrentOrganization: Dispatch<
    SetStateAction<AppContextData["currentOrganization"]>
  >;

  selectedCheckInId: string;
  setselectedCheckInId: (e: string) => void;
};
export const AppContext = createContext<AppContextData>({} as AppContextData);

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error("useAppContext must be used within a AppContextProvider");
  }
  return context;
};

interface Props {
  env: EnvConfig | undefined;
  children: React.ReactNode;
}

const AppProvider: FC<Props> = ({ children, env }) => {
  let location = useLocation();
  const navigate = useNavigate();
  const { query } = useApolloClient();
  const { logout } = useContext(ApolloClientContext);

  const [isDarkMode, setIsDarkMode] =
    useState<AppContextData["isDarkMode"]>(false);
  const [currentUser, setCurrentUser] =
    useState<AppContextData["currentUser"]>(undefined);
  const [currentClient, setCurrentClient] =
    useState<AppContextData["currentClient"]>(undefined);
  const [currentEmployee, setCurrentEmployee] =
    useState<AppContextData["currentEmployee"]>(undefined);
  const [currentAdmin, setCurrentAdmin] =
    useState<AppContextData["currentAdmin"]>(undefined);
  const [currentOrganization, setCurrentOrganization] =
    useState<AppContextData["currentOrganization"]>(undefined);
  const [currentLocation, setCurrentLocation] =
    useState<AppContextData["currentLocation"]>(undefined);
  const [activeTabKey, setActiveTabKey] =
    useState<AppContextData["activeTabKey"]>("Organization");
  const [currentPageTitle, setCurrentPageTitle] =
    useState<AppContextData["currentPageTitle"]>(undefined);
  const [workspaceDimensions, setWorkspaceDimensions] =
    useState<AppContextData["workspaceDimensions"]>(undefined);
  // used in checkIn Queue multiple modal cannot open at time so need to save id
  const [selectedCheckInId, setselectedCheckInId] = useState("");

  // update Branding information
  const locationOfferingId = sessionStorage.getItem(
    StorageKeyName.LocationOfferingId
  );
  const locationId = sessionStorage.getItem(StorageKeyName.LocationId);
  const { setBranding, orgIdLogin } = useBrandingContext();
  useEffect(() => {
    if (currentOrganization?.organizationBranding) {
      setBranding(currentOrganization.organizationBranding);
    }
  }, [currentOrganization]);

  useEffect(() => {
    if (currentClient?.organization?.organizationBranding) {
      setBranding(currentClient?.organization?.organizationBranding);
    }
  }, [currentClient]);

  useEffect(() => {
    getUser();
  }, []);

  useEffect(() => {
    setselectedCheckInId("");
  }, [currentLocation]);

  useEffect(() => {
    if (currentAdmin || currentEmployee) {
      if (currentAdmin) {
        getOrganization(currentAdmin.organization.id);
      } else if (currentEmployee) {
        getOrganization(currentEmployee.organization.id);
      }
    }
  }, [currentAdmin, currentEmployee]);

  const getUser = async () => {
    try {
      // pass organizationId on getUser method
      const orgLoginId = localStorage.getItem(StorageKeyName.OrgLoginId);
      let response = await query<GetUserQuery, GetUserQueryVariables>({
        query: GetUser,
        ...(orgLoginId && { variables: { orgId: orgLoginId } }),
        fetchPolicy: "no-cache",
      });
      if (response.data.userOne) {
        setCurrentUser(response.data.userOne);
        if (response.data.userOne.role === UserRole.Client) {
          getClient();
        }
        if (response.data.userOne.role === UserRole.Employee) {
          getEmployee();
          // navigate(`/`);
        }
        if (response.data.userOne.role === UserRole.Admin) {
          getAdmin();
          // navigate(`/`);
        }
      }
    } catch (e) {
      // if we catch here then we cant find a user with the current token so invalidate token and logout
      logout();
    }
  };

  const [updateClientOrg] = useUpdateClientOrgMutation({});

  const getClient = async () => {
    let response = await query<GetClientQuery, GetClientQueryVariables>({
      query: GetClient,
      fetchPolicy: "no-cache",
    });
    if (response.data.clientOne) {
      const { organization, id } = response.data.clientOne;
      setCurrentClient(response.data.clientOne);

      // check for BuyAgreementId and lead then navigate to /signAgreementPage and clear locationOfferingId
      if (
        locationOfferingId &&
        response.data.clientOne?.memberStatus === MemberStatus.Lead
      ) {
        // once navigate to /signAgreementPage remove offering id
        sessionStorage.removeItem(StorageKeyName.LocationOfferingId);
        sessionStorage.removeItem(StorageKeyName.LocationId);
        navigate(`${PATH_NAMES.AGREEMENT_OVERVIEW}/${locationOfferingId}`);
      }

      if (!organization?.id && orgIdLogin) {
        // set orgId & locationId that were saved in orgIdLogin if not saved on signup
        updateClientOrg({
          variables: { organizationId: orgIdLogin, id, locationId },
        });
      }
      const userObj = {
        client: true,
        organizationId: organization?.id || orgIdLogin,
      };
      localStorage.setItem("user", JSON.stringify(userObj));

      const { defaultLocation, locations } = response.data.clientOne;
      if (defaultLocation || locations?.length) {
        setCurrentLocation(defaultLocation || locations?.[0]);
      }
    }
  };

  const getEmployee = async () => {
    let response = await query<GetEmployeeQuery, GetEmployeeQueryVariables>({
      query: GetEmployee,
      fetchPolicy: "no-cache",
    });
    if (response.data.employeeOne) {
      setCurrentEmployee(response.data.employeeOne);
      !currentLocation?.id &&
        response.data.employeeOne.activeLocation &&
        setCurrentLocation(response.data.employeeOne.activeLocation);
    }
  };

  const getAdmin = async () => {
    let response = await query<GetAdminQuery, GetAdminQueryVariables>({
      query: GetAdmin,
      fetchPolicy: "no-cache",
    });
    if (response.data.adminOne) {
      // for admin remove OrgLoginId
      removeOrgLoginId();
      setCurrentAdmin(response.data.adminOne);
    }
  };

  const getOrganization = async (id: string) => {
    let response = await query<
      GetOrganizationQuery,
      GetOrganizationQueryVariables
    >({
      query: GetOrganization,
      fetchPolicy: "no-cache",
      variables: {
        id,
      },
    });
    if (response.data.organizationOne) {
      setCurrentOrganization(response.data.organizationOne);

      let locations = response.data.organizationOne.locations;
      if (
        !locations.find((loc) => loc.id == currentLocation?.id) &&
        currentUser?.role !== UserRole.Employee
      ) {
        setCurrentLocation(locations.length > 0 ? locations[0] : undefined);
      }
    }
  };

  const refetchUserData = () => {
    getUser();
  };

  let changeArrTab = {
    [TAB_LIST.HOME]: { link: ROUTES.HOME },
    [TAB_LIST.DASHBOARD]: { link: ROUTES.DASHBOARD },
    [TAB_LIST.REPORTS]: { link: ROUTES.REPORTS },
    [TAB_LIST.ORGANIZATION]: { link: ROUTES.ORGANIZATION },
    [TAB_LIST.LOCATION]: { link: ROUTES.LOCATION },
    [TAB_LIST.GENERAL]: {
      link: `${ROUTES.LOCATION}/${PATH_NAMES.GENERAL_LOC}`,
    },
    [TAB_LIST.LOCATION_SERVICE]: { link: ROUTES.LOCATION_SERVICE },
    [TAB_LIST.POLICY]: { link: ROUTES.POLICY },
    [TAB_LIST.EMPLOYEE]: { link: ROUTES.EMPLOYEE },
    [TAB_LIST.SERVICE]: { link: ROUTES.SERVICE },
    [TAB_LIST.TIME_CLOCK]: { link: ROUTES.TIME_CLOCK },
    [TAB_LIST.STATIONS]: { link: ROUTES.STATIONS },
    [TAB_LIST.ROOM]: { link: ROUTES.ROOM },
    [TAB_LIST.DISCOUNTS]: { link: ROUTES.DISCOUNTS },
    [TAB_LIST.CLIENT]: { link: ROUTES.CLIENT },
    [TAB_LIST.AGREEMENT]: { link: ROUTES.AGREEMENT },
    [TAB_LIST.PACKAGE]: { link: ROUTES.PACKAGE },
    [TAB_LIST.ORG_AGREEMENT]: { link: ROUTES.ORG_AGREEMENT },
    [TAB_LIST.ROLES]: { link: ROUTES.ROLES },
    [TAB_LIST.ORG_PACKAGE]: { link: ROUTES.ORG_PACKAGE },
    [TAB_LIST.CALENDER]: { link: ROUTES.CALENDER },
    [TAB_LIST.PRODUCT]: { link: ROUTES.PRODUCT },
    [TAB_LIST.REQUESTS]: { link: ROUTES.REQUESTS },
    [TAB_LIST.CALENDER_RECURRING_EVENTS]: {
      link: ROUTES.CALENDER_RECURRING_EVENT,
    },

    // Client App
  };

  let clientArrTab = {
    [CLIENT_TAB_LIST.HOME]: {
      link: `${CLIENT_ROUTES.HOME}`,
    },
    [CLIENT_TAB_LIST.PROFILE]: {
      link: `${CLIENT_ROUTES.PROFILE}`,
    },
    [CLIENT_TAB_LIST.MY_CALENDAR]: {
      link: `${CLIENT_ROUTES.MY_CALENDAR}`,
    },
    [CLIENT_TAB_LIST.MY_ACCOUNTS]: {
      link: `${CLIENT_ROUTES.MY_ACCOUNTS}`,
    },
    [CLIENT_TAB_LIST.MY_RECORDS]: {
      link: `${CLIENT_ROUTES.MY_RECORDS}`,
    },
  };

  useEffect(() => {
    let getKey = location.pathname.split("/")[1];
    // handle case for Time Clock has space in between so I handled it using _ (underscore)
    if (getKey.includes("_")) {
      let _arr = getKey.split("_");
      setActiveTabKey(
        `${_arr[0].charAt(0).toUpperCase()}${_arr[0].slice(1)} ${_arr[1]
          .charAt(0)
          .toUpperCase()}${_arr[1].slice(1)}`
      );
    } else {
      setActiveTabKey(getKey.charAt(0).toUpperCase() + getKey.slice(1));
    }
  }, []);

  const changeTab = (key: string, id?: string) => {
    setActiveTabKey(key);
    if (!changeArrTab[key]) {
      console.log("Changed to unhandled tab");
    } else {
      navigate(changeArrTab[key]?.link);
    }
  };

  const changeClientTab = (key: string, id?: string) => {
    setActiveTabKey(key);
    if (!clientArrTab[key]) {
      console.log("Changed to unhandled tab");
    } else {
      navigate(clientArrTab[key]?.link);
    }
  };

  return (
    <AppContext.Provider
      value={{
        activeTabKey,
        currentAdmin,
        currentClient,
        currentEmployee,
        setCurrentEmployee,
        currentLocation,
        currentOrganization,
        currentPageTitle,
        currentUser,
        env,
        isDarkMode,
        workspaceDimensions,
        changeTab,
        changeClientTab,
        refetchUserData,
        setCurrentLocation,
        setCurrentPageTitle,
        setCurrentUser,
        setIsDarkMode,
        setWorkspaceDimensions,
        getEmployee,
        setCurrentOrganization,
        selectedCheckInId,
        setselectedCheckInId,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export default AppProvider;
